/**
 * SplashOverlay - Premium animated splash screen
 * 
 * Renders a full-screen overlay with:
 * - Compass spring-bounce entrance with rotation
 * - Golden ambient glow + expanding pulse rings
 * - Compass needle "finding north" settle animation
 * - "Miac" letter-by-letter stagger reveal
 * - "Prove Your Habits" tagline fade-in
 * - Smooth fade-out exit to reveal the app
 * 
 * Supports light/dark mode via Tailwind classes.
 * On native, hides the Capacitor splash screen seamlessly.
 */

import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Capacitor } from '@capacitor/core'
import { hideSplashScreen } from '@/lib/native'

interface SplashOverlayProps {
  onComplete: () => void
}

// Duration before exit animation starts (ms)
const SPLASH_DURATION = 2600

// Pulse ring configuration
const RINGS = [0, 1, 2] as const

export function SplashOverlay({ onComplete }: SplashOverlayProps) {
  const [show, setShow] = useState(true)

  useEffect(() => {
    // On native, hide the Capacitor splash screen immediately
    // so the React animated splash takes over seamlessly
    // (both share the same background color = no flash)
    if (Capacitor.isNativePlatform()) {
      hideSplashScreen().catch(console.error)
    }

    // After entry animations complete, trigger exit
    const timer = setTimeout(() => setShow(false), SPLASH_DURATION)
    return () => clearTimeout(timer)
  }, [])

  const handleExitComplete = useCallback(() => {
    onComplete()
  }, [onComplete])

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {show && (
        <motion.div
          key="splash-overlay"
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#f8fafc] dark:bg-[#020617] overflow-hidden"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          aria-hidden="true"
        >
          {/* ── Ambient golden glow ── */}
          <motion.div
            className="absolute pointer-events-none"
            style={{
              width: 180,
              height: 180,
              background:
                'radial-gradient(circle, rgba(251,191,36,0.28) 0%, rgba(251,191,36,0.08) 50%, transparent 70%)',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 2.8, 2.4],
              opacity: [0, 1, 0.6],
            }}
            transition={{ delay: 0.3, duration: 1.6, ease: 'easeOut' }}
          />

          {/* ── Dark mode glow variant (slightly more visible) ── */}
          <motion.div
            className="absolute pointer-events-none hidden dark:block"
            style={{
              width: 180,
              height: 180,
              background:
                'radial-gradient(circle, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0.05) 50%, transparent 70%)',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 3.2, 2.8],
              opacity: [0, 1, 0.8],
            }}
            transition={{ delay: 0.3, duration: 1.6, ease: 'easeOut' }}
          />

          {/* ── Expanding pulse rings ── */}
          {RINGS.map((i) => (
            <motion.div
              key={`ring-${i}`}
              className="absolute rounded-full pointer-events-none border-[1.5px] border-amber-400/20 dark:border-amber-300/15"
              style={{ width: 112, height: 112 }}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{
                scale: [0.5, 3.5],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                delay: 0.6 + i * 0.4,
                duration: 2,
                ease: 'easeOut',
              }}
            />
          ))}

          {/* ── Compass icon ── */}
          <motion.div
            className="relative z-10"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: 'spring',
              stiffness: 180,
              damping: 14,
              mass: 1,
            }}
          >
            {/* Needle-settle oscillation (compass finding north) */}
            <motion.div
              animate={{ rotate: [0, 15, -8, 4, -2, 0] }}
              transition={{
                delay: 0.7,
                duration: 1.2,
                ease: 'easeInOut',
              }}
            >
              {/* App-icon style container: black bg blends with the PNG, 
                  rounded corners clip to iOS icon shape, glow ring for depth */}
              <div className="w-32 h-32 rounded-[22%] bg-black overflow-hidden shadow-2xl shadow-amber-500/20 ring-1 ring-white/10">
                <img
                  src="/app-logo.png"
                  alt=""
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              </div>
            </motion.div>

            {/* Shimmer highlight across compass */}
            <motion.div
              className="absolute inset-0 pointer-events-none rounded-[22%] overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <motion.div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.35) 50%, transparent 80%)',
                }}
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ delay: 1.2, duration: 0.8, ease: 'easeInOut' }}
              />
            </motion.div>
          </motion.div>

          {/* ── App name: "Miac" with letter stagger ── */}
          <div className="mt-8 flex overflow-hidden">
            {'Miac'.split('').map((letter, i) => (
              <motion.span
                key={i}
                className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  delay: 1.0 + i * 0.1,
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                }}
              >
                {letter}
              </motion.span>
            ))}
          </div>

          {/* ── Tagline ── */}
          <motion.p
            className="mt-3 text-sm font-medium tracking-widest uppercase text-slate-400 dark:text-slate-500"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.6, ease: 'easeOut' }}
          >
            Prove Your Habits
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
